import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IconArrowLeft, IconPlus } from '@tabler/icons-react';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageForm from '../../components/layout/PageForm';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import PaginatedAsyncSelect from '../../components/form/PaginatedAsyncSelect';

const asList = (response) => response?.results || response || [];

export default function StockItemAdd() {
  const { warehouseId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantityOwned, setQuantityOwned] = useState(0);
  const [quantityAvailable, setQuantityAvailable] = useState(0);
  const [error, setError] = useState(null);
  const { data: warehouse, isLoading } = useQuery({
    queryKey: ['warehouse', warehouseId],
    queryFn: () => api.get(`warehouses/api/warehouses/${warehouseId}/`),
  });
  const productOption = (entry) => ({ value: entry.id, label: `${entry.name} (${entry.sku})`, trackingType: entry.tracking_type });
  const createStockItem = useMutation({
    mutationFn: () => api.post('warehouses/api/stock-items/', {
      stock: Number(warehouseId),
      product: product.value,
      quantity_owned: Number(quantityOwned),
      quantity_available: Number(quantityAvailable),
    }),
    onSuccess: () => navigate(`/warehouses/stock/${warehouseId}`),
    onError: (requestError) => {
      const data = requestError?.response?.data;
      setError(data?.non_field_errors?.[0] || data?.detail || 'Zásobu se nepodařilo vytvořit. Produkt může být v tomto skladu již založený.');
    },
  });
  const submit = (event) => {
    event.preventDefault();
    if (!product) {
      setError('Vyberte produkt z katalogu.');
      return;
    }
    if (Number(quantityAvailable) > Number(quantityOwned)) {
      setError('Dostupné množství nemůže být vyšší než vlastněné množství.');
      return;
    }
    setError(null);
    createStockItem.mutate();
  };

  if (isLoading) {
    return <PageShell pretitle="Sklad" title="Přidat zásobu"><PageSection><CenteredLoader /></PageSection></PageShell>;
  }

  return <PageShell
    pretitle="Inventář"
    title={`Přidat zásobu${warehouse?.name ? `: ${warehouse.name}` : ''}`}
    description="Vyberte produkt z katalogu a nastavte jeho množství v tomto skladu."
    actions={<Button variant="outline-secondary" onClick={() => navigate(`/warehouses/stock/${warehouseId}`)}><IconArrowLeft size={16} className="me-1" />Zpět</Button>}
  >
    <PageForm
      title="Skladová zásoba"
      onSubmit={submit}
      footer={<><Button variant="outline-secondary" onClick={() => navigate(`/warehouses/stock/${warehouseId}`)}>Zrušit</Button><Button type="submit" disabled={!product || createStockItem.isPending}><IconPlus size={16} className="me-1" />Uložit zásobu</Button></>}
    >
      {error && <Alert variant="danger" className="mb-0">{error}</Alert>}
      <Form.Group>
        <Form.Label>Produkt z katalogu</Form.Label>
        <PaginatedAsyncSelect endpoint="warehouses/api/products/" value={product} onChange={setProduct} mapOption={productOption} placeholder="Hledat název nebo SKU" />
      </Form.Group>
      {product && <div className="text-secondary small">Typ sledování: {product.trackingType}</div>}
      <div className="row">
        <Form.Group className="col-md-6">
          <Form.Label>Vlastněné množství</Form.Label>
          <Form.Control type="number" min="0" value={quantityOwned} onChange={(event) => setQuantityOwned(event.target.value)} />
        </Form.Group>
        <Form.Group className="col-md-6">
          <Form.Label>Dostupné množství</Form.Label>
          <Form.Control type="number" min="0" value={quantityAvailable} onChange={(event) => setQuantityAvailable(event.target.value)} />
        </Form.Group>
      </div>
    </PageForm>
  </PageShell>;
}
